import { useEffect, useState } from 'react';

import { Globe02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import autosize from 'autosize';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import { SupportedLanguagesModal } from 'components/SupportedLanguagesModal/SupportedLanguagesModal';

import styles from './Textarea.module.scss';

export const Textarea = ({
  name,
  autoComplete,
  placeholder,
  defaultValue,
  minLength,
  maxLength,
  validators = {},
  dynamicHeight,
  rows,
  register = () => {},
  error = '',
  noErrorMessage,
  className,
  inputProps = {},
  brand = {},
  allowTranslation = false,
  dataTestId,
}) => {
  const { t } = useTranslation();
  const [showSupportedLanguagesModal, setShowSupportedLanguagesModal] = useState(false);
  const [showSupportedLanguagesText, setShowSupportedLanguagesText] = useState(true);

  useEffect(() => {
    if (dynamicHeight) {
      autosize(document.querySelectorAll('.' + className));
    }
  }, [dynamicHeight]);

  return (
    <>
      <SupportedLanguagesModal
        visible={showSupportedLanguagesModal}
        onClose={() => setShowSupportedLanguagesModal(false)}
      />
      {allowTranslation && (
        <div className="u-relative">
          <span
            className="u-absolute u-right-8 u-top-12 secondary-text u-font-size-x-small u-font-weight-400 d-flex align-items-center u-gap-small justify-content-end u-pointer surface-white u-padding-x--small u-padding-y--x-small u-border u-border-radius u-drop-shadow--xs"
            style={{ color: brand?.color_link }}
            onClick={() => setShowSupportedLanguagesModal(true)}>
            <HugeiconsIcon size="12" icon={Globe02Icon} strokeWidth="1.5" />
            {showSupportedLanguagesText ? t('components.form.supportedLanguages') : ''}
          </span>
        </div>
      )}
      <textarea
        name={name}
        autoComplete={autoComplete}
        placeholder={placeholder}
        defaultValue={defaultValue}
        minLength={minLength}
        maxLength={maxLength}
        {...register(name, validators)}
        className={cn(styles.root, {
          [className]: className,
          'mb-2': error,
        })}
        {...inputProps}
        rows={rows ?? inputProps?.rows}
        onFocus={() => {
          setShowSupportedLanguagesText(false);
          inputProps?.onFocus?.();
        }}
        data-testid={dataTestId}
      />
      {error && !noErrorMessage && (
        <FadeInMotion key={name}>
          <ValidationAlert show={true} message={error} />
        </FadeInMotion>
      )}
    </>
  );
};

Textarea.propTypes = {
  name: PropTypes.string,
  autoComplete: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  validators: PropTypes.object,
  dynamicHeight: PropTypes.bool,
  /** Initial visible rows (e.g. 6 for long_text / client_specific_instructions). */
  rows: PropTypes.number,
  register: PropTypes.func,
  error: PropTypes.string,
  noErrorMessage: PropTypes.bool,
  className: PropTypes.string,
  inputProps: PropTypes.object,
};
